<!-- TownMappingView.vue -->
<template>
    <div class="app-container">
      <header-bar></header-bar>
  
      <div class="content-wrapper">
        <div class="empty-left"></div>
        <div class="left-sidenotes">
          <h2 class="settings-header">Content</h2>
          <p class="subhead" :class="{ active: activeSection === 'Welcome' }" @click="setActiveSection('Welcome')">
            Welcome
          </p>
          <p class="subhead" :class="{ active: activeSection === 'Roads' }" @click="setActiveSection('Roads')">
            Roads
          </p>
          <p class="subhead" :class="{ active: activeSection === 'Grants' }" @click="setActiveSection('Grants')">
            Grants
          </p>
          <p class="subhead" :class="{ active: activeSection === 'Personnel' }" @click="setActiveSection('Personnel')">
            Personnel
          </p>
          <router-link to="/" class="subhead">
            Sign Up For Ansable
          </router-link>
        </div>
    
        <div class="main">
          <div class="mobile-select">
            <select v-model="activeSection" @change="handleMobileSelection">
              <option value="Welcome">Click here to choose topic</option>
              <option value="Roads">Roads</option>
              <option value="Grants">Grants</option>
              <option value="Personnel">Personnel</option>
              <option value="SignUp">Sign Up</option>
            </select>
          </div>
    
          <div v-if="loading">Loading...</div>
          <div v-else class="content">
            <div v-if="activeSection === 'Welcome'" v-html="renderMarkdown(content.Welcome)"></div>
            <div v-else-if="activeSection === 'Roads'" v-html="renderMarkdown(content.Roads)"></div>
            <div v-else-if="activeSection === 'Grants'" v-html="renderMarkdown(content.Grants)"></div>
            <div v-else-if="activeSection === 'Personnel'" v-html="renderMarkdown(content.Personnel)"></div>
          </div>
        </div>
        <div class="right-sidenotes"></div>
      </div>
    </div>
  </template>
  
  <script>
  import HeaderBar from './RevisedHeader.vue';
  /* import { doc, getDoc } from 'firebase/firestore';
  import { db } from '@/firebase'; */
  import { marked } from 'marked';
  import DOMPurify from 'dompurify';
  
  export default {
    name: 'TownMappingView',
    components: {
      HeaderBar
    },
    data() {
      return {
        activeSection: 'Welcome',
        loading: false,
        content: {
          Welcome: `# Welcome to our Goshen 2024 Review

Ahead of Goshen's town meeting, I wanted to get a refresher on the big topics discussed in town over the last year. That's not always easy to do, so my partner and I created a tool that combs through and summarizes all of the selectboard meeting agendas, minutes, and audio recordings. We thought it would be a fun project that combined my skills as a newspaper editor with his technical expertise.

We were really happy with the output, and decided to share for anyone else who would like to take a look. We've edited the content slightly to ensure its accuracy and relevance to the town meeting agenda. Use the left sidebar to navigate the sections.

If you find this helpful, we've set the tool up to run for every selectboard meeting in Goshen and Brandon, as well as the OVUUSD. This should help folks stay informed week to week! Use the line in the left column (or the selector on mobile) to sign up.

-Allie`,

          Roads: `# Selectboard Road Discussions Overview

GOSHEN, VT – The town of Goshen faced a challenging year managing its roads and related infrastructure, marked by extensive truck repairs, ongoing road maintenance projects, and budgetary pressures, according to selectboard meeting minutes from February 2024 to January 2025.

The aging condition of the town's truck fleet, the "green truck" and "black truck," dominated much of the year's discussions, along with efforts to secure funding for critical road improvements.

### Truck Troubles: A Recurring Theme

The year began with immediate concerns about the town's aging trucks. The rusted "green truck" became a central point of debate beginning in February. Initial discussions focused on obtaining multiple written assessments of the large truck (Feb. 20, Feb. 29 meetings), but finding mechanics to fully assess it proved difficult (Feb. 29 meeting). The select board ultimately sought estimates to decide whether to repair, sell, or scrap it (April 22 meeting).

Ultimately, the assessment revealed an estimated $57,000 repair cost, with significant rust and mechanical problems. The recommendation was replacement or leasing options (July 22, Aug. 26, Sept. 9 meetings). The board authorized repairs, not to exceed $5,000, with work performed by Earl's (Sept. 23, Oct. 8, Oct. 14 meetings), but the truck continued to present challenges as the year went by. Additional problems were identified (Nov. 11 meeting) and by year's end, a new drag chain was needed (Nov. 25 meeting).

The "black truck" also required significant attention. In February, reports surfaced about its rusted cross members and failing scissor hoist (Feb. 29 meeting). Temporary repairs were undertaken by local welder Andrew Atwood to keep the truck operational for gravel spreading (Feb. 29, March 11 meetings), with the work completed in late March (March 25 meeting). The temporary fix was projected to last two to four years, delaying the need for a costly new box (March 25). However, the selectboard later authorized seeking estimates for a new box, recognizing the ongoing maintenance needs (Oct. 8, Oct. 14 meetings). The black truck was serviced, and issues with the oil pan and power steering fluid were identified (Oct. 14 meeting).

The ongoing truck problems prompted the formation of a truck purchasing committee in April comprised of Tammy Walsh, Dave Sabatini, Jeff Whiting, and mechanic Brad Nadeau (April 8 meeting), to research purchase options and define the town's needs (April 22, June 3 meetings). The urgency of finding a replacement, especially with winter approaching, was emphasized (April 22, June 10 meetings).

### Road Maintenance and Improvement Projects

Beyond the truck issues, the selectboard addressed a range of road maintenance and improvement projects throughout the year.

#### Town Hill Road

A significant focus was the multiphase Town Hill Road project. In April, the board approved a phased approach, starting with culvert replacements and ditching in the current year, followed by repaving the next (April 22 meeting). Brian Sanderson from VTrans was involved in the planning (April 22 meeting). Jeff developed a draft bid specification for phase 1, focusing on culvert replacement (June 24 meeting). A pre-bid meeting and walk-through with interested bidders were planned (June 24 meeting). Sanderson recommended hiring an engineering firm to act as project manager (Aug. 7 meeting). Jeff Cathcart was ultimately appointed project manager in November.

#### Dutton Brook Road

The town received a $7,000 Grant-in-Aid for stormwater management improvements on Dutton Brook Road, requiring a 20% town match (June 3 meeting). Jeff Whiting and Diane O'Classen planned to inspect the road to identify specific culvert needs (June 10 meeting). Work on this project, including ditching, was slated for August (July 15 meeting). The selectboard voted to increase the town's equipment rental rate to $50 per hour, retroactive to July 1, 2024, to simplify reimbursement (Aug. 26 meeting). Progress was made, culverts were installed (Aug. 26, Sept. 9 meetings). The project approached completion in September, with some extra excavator time potentially available for other town ditching work (Sept. 9, Sept. 23 meetings).

#### Additional Road Maintenance Topics

* **Roadside maintenance**: Roadside brush clearing was a recurring concern (March 11, March 25, April 22 meetings). The board recognized the need for additional manpower (March 11, March 25 meetings), discussing the potential hiring of Dave Sabatini as a temporary town employee for roadside maintenance (April 22 meeting). Road crew members worked on roadside tree clearing, with about 70% completed. A hazardous tree also needed removal, with the selectboard responsible for arranging and funding the process (June 3 meeting). Roadside mowing was approved for bid (March 25 meeting), and the board later approved obtaining written bids for the work (April 22 meeting). A bid from Lem Palmer, the previous year's contractor, was received and accepted (June 24 meeting).

* **Road sweeping**: The board discussed hiring Gary Dubois for road sweeping (April 22 meeting) and cleaning bridges without sweeping debris into the water (June 3 meeting).

* **Bridge repair**: A damaged bridge guardrail was identified, prompting discussion about insurance covering the damages, along with the repairs and installation of a new guardrail (June 3, June 10, June 24, July 22, Aug. 7 meetings). The repair was completed by August (Aug. 7 meeting).

* **Grading**: Regular road grading was performed throughout the year, with specific mentions of work on Goshen Ripton Road, Silver Lake Road, and Hayes Road (June 10 meeting). Further grading was planned for Silver Lake Road and Goshen Ripton Road (July 22 meeting).

* **Welland's Cemetery**: In September, gravel was hauled (July 15 meeting). Jeff worked with Ken Brown to complete more work (Oct. 14 meeting).

### Financial and Budgetary Considerations

Financial constraints and budgetary concerns were evident throughout the year.

* **Over budget**: In March, road payments were reported to be $10,000 over budget, partly due to material expenses (March 11 meeting).

* **Truck purchase funding**: The limited availability of funds for a new truck was a recurring issue. Discussions included using funds from a liquid savings account and CDs (March 11 meeting).

* **ARPA funds**: The status of ARPA funds was reviewed, with several projects still in progress and funds needing to be encumbered by Dec. 31, 2024 (Sept. 9, Oct. 8 meetings).

* **Road budget 2025-2026**: The proposed road budget included staying at $35,000 for summer materials and increasing winter materials to $25,000 (Nov. 25 meeting).

* **Contractor insurance**: The need for contractors working for the town to provide certificates of insurance was reiterated (April 22 meeting).

### Other Notable Issues

* **Hazardous tree removal**: A hazardous maple tree near the town office prompted discussions about removal, with the board deciding to solicit bids (June 24, July 22, Aug. 7 meetings). The specifications for removal were adjusted to exclude stump grinding and involve the town crew in fence removal (Aug. 26 meeting).

* **Green Mountain Power Project**: The Green Mountain Power Diversion Dam project was expected to generate significant truck traffic, with Green Mountain Power responsible for related road maintenance (Sept. 23, Oct. 8 meetings). The project was completed for the year, with Green Mountain Power providing a substantial stockpile of material to the town (Nov. 25 meeting).

* **Logging project**: A logging project on South Hill Road raised concerns about overweight permits, potential road damage, and the need for coordination between the town, logger, and state (Jan. 13, 2025, Jan. 27, 2025 meetings). The selectboard will require the logger to shore up the bridge (Jan. 27, 2025 meeting).

* **Missing equipment**: The road foreman raised concerns about missing equipment and tools from the town garage (Oct. 14 meeting).

### Personnel Changes

The road crew saw several staffing changes throughout the year, the biggest being a change in the road foreman from Tammy Walsh to Jeff Cathcart.`,
          Grants: `# Goshen Grant Funding and Infrastructure Report

GOSHEN, VT – The town of Goshen focused on securing grant funding, managing road projects, and addressing infrastructure needs throughout 2024 and early 2025, according to selectboard meeting minutes. Key areas of activity included stormwater management, energy efficiency initiatives, road maintenance, and project management.

### Stormwater Management and Grants-in-Aid

The state's Grants-in-Aid program was a central element of the town's stormwater management efforts. Goshen received a $7,000 grant in June for stormwater management, specifically targeting runoff issues affecting Lake Champlain (June 3 meeting). The grant required an 80/20 match from the town and focused on a 100-meter section of road and drainage near Dutton Brook (June 3 meeting). The project, encompassing culvert upgrades, ditching, and berm removal, had a completion deadline of Sept. 30, 2024, and included updating the Vermont Culverts database (June 10, July 22 meetings).

The Better Back Roads Grant was also discussed in November as a funding method to replace two culverts on Goshen Ripton Road. This grant entailed an $11,000 match for the town (Nov. 25 meeting).

### Energy Efficiency Initiatives

Energy efficiency became a significant focus area. The town allocated $3,500 to the Climate Economy Action Committee (CEAC) in June to train "navigators" (June 24 meeting). These navigators were tasked with assisting residents in conducting energy assessments and identifying funding opportunities for home energy upgrades (June 24 meeting). The selectboard considered a flyer mailer to inform taxpayers about energy programs.

#### Municipal Energy Resilience Program (MERP) Grant

The selectboard actively explored the Municipal Energy Resilience Program (MERP) grant with the Addison County Regional Planning Commission assisting in developing a request for proposals (Aug. 7 meeting). The application process was anticipated for October, with grant approvals expected by year's end and construction completed by 2028 (Aug. 7 meeting). Questions arose regarding the town's existing generator and its capacity to handle future electrical needs, particularly with planned energy upgrades, including heat pumps (Aug. 7 meeting). This prompted a postponement of generator installation until final MERP grant plans were complete (Aug. 7 meeting). The use of ARPA funds for a generator was later confirmed (Aug. 26, Sept. 9 meeting).

Later discussions involved using MERP funds to repair the town hall (Fall 2024 meetings). Vague language created uncertainty about whether structural issues would be covered. In November, concerns regarding the drainage project led to a halt of the project and a structural engineer was contacted (Nov. 14 meeting).

### ARPA Funds Utilization

American Rescue Plan Act (ARPA) funds were strategically utilized. Projects included $3,000 for Welland's Cemetery road improvements (Aug. 7 meeting), and various allocations in November for a kitchen hood fan, a rug, loader forks, road crew safety gear, an information kiosk, generator installation, tools, and a picnic table (Nov. 25 meeting). The treasurer reported in September that a balance of $168.91 remained in ARPA funds, with a spending deadline of Dec. 31, 2024 (Sept. 9 meeting).

### Road Maintenance and Major Projects

Road maintenance and infrastructure projects received significant attention. $200,000 was confirmed for the Town Hill Road Grant in October, with an additional $117,000 from the Paving Fund allocated to the road account (July 8, Oct. 28 meeting). A project manager was appointed for the Town Hill project, with compensation set at $50 per hour (Oct. 28 meeting).

The Dutton Brook Road project, linked to the initial Grants-in-Aid award, was a recurring topic. Concerns about the project's timeline arose in August, particularly with a transition in the road foreman position (Aug. 26 meeting). An excavator arrived for the project, and a transition meeting was planned to ensure smooth handover of project management (Aug. 26 meeting). The project's completion deadline was Sept. 30, with a requirement to assess the road's sub-base for future paving (July 8 meeting).

### Challenges and Transitions

The transition of the road foreman position created uncertainties regarding grant management, particularly the Dutton Brook Road project (Aug. 26 meeting). This prompted discussions about a transition meeting and raised questions about the new foreman's readiness (Aug. 26 meeting).

Project management also presented challenges. The need for external expertise was highlighted by the recommendation to hire an engineering company to manage the Town Hill Road project (Aug. 26 meeting).

### Future Initiatives

As Goshen moves into 2025, several initiatives are underway. The town is seeking proposals for assistance with the Local Hazard Mitigation Plan (July 22, Aug. 12 meetings). The fiscal year 2025 Grants-in-Aid award is secured, and plans for the Town Hill Road project are progressing, with a bid package prepared (Jan. 27, 2025 meeting). The selectboard continues to explore other grant opportunities, including state programs offering substantial funding for infrastructure improvements.`,
          Personnel: `# Goshen Personnel Changes and Governance Report

GOSHEN, VT – Goshen experienced significant personnel changes and governance challenges throughout 2024 and early 2025, according to selectboard meeting minutes. Key areas of concern included selectboard instability, turnover in critical positions (treasurer, road foreman), and road crew staffing issues.

### Selectboard Instability and Citizen Petitions

The year began and ended with a three-member selectboard, but internal conflict and citizen concerns regarding transparency and effectiveness emerged as recurring themes. Discussions in February addressed potential conflicts of interest involving a selectboard member who also served as road foreman (Feb. 20 meeting).

Thomasina Magoon was nominated as selectboard chair in March (March 11 meeting). In June, citizen concerns about the board's organization and the chairperson selection process led to a motion to appoint a different member as chair (June 3 meeting). The lack of a selectboard clerk, who had previously resigned, was noted as a major barrier to local government efficiency, particularly regarding taking the meeting minutes (June 3 meeting).

A citizen petition to expand the selectboard from three to five members gained traction in June (June 10 meeting). Proponents argued that a larger board would alleviate the workload and improve decision-making. Debates arose concerning the town's aging population, the potential for increased conflict, and overall board effectiveness. A special election on the potential of expanding the selectboard was set for July. Ultimately, the town will remain with three selectboard members.

On June 10, Thomasina Magoon resigned from the selectboard. The board then appointed Diane O'Classen as a temporary replacement, bypassing the policy of requiring a 10-day vacancy posting, citing the urgency of maintaining a functional board (June 10 meeting). This appointment was intended to be temporary, lasting until the general election.

In August, Will Mathis announced his plan to retire from the selectboard, effective in November (Aug. 26 meeting). More change came to the board the following month with Tammy Walsh's immediate resignation from the selectboard on Sept. 9. The next meeting, Thomasina Magoon was appointed to fill the vacant selectboard seat until the November election (Sept. 23 meeting).

On election day, Diane O'Classen, Thomasina Magoon and Timmi Moffi were voted in as Goshen's newest selectboard. O'Classen was appointed to the role of board chair (Nov. 11 meeting).

### Road Crew Challenges and Foreman Turnover

The road foreman position and road crew staffing were consistent topics of discussion throughout the year. Early discussions in February focused on the need for additional CDL drivers and resource allocation.

Concerns regarding the road foreman's role, transparency, financial oversight, and the relationship with private contractors, particularly Goshen Mountain Landscaping, emerged in March.

Tammy Walsh served as road foreman, but her dual role on the selectboard and potential conflict of interest were questioned (Aug. 7 meeting). Citizens and some board members advocated for the appointment of Jeff Cathcart as temporary road foreman (July 22 meeting).

Jeff Cathcart was eventually appointed temporary road foreman in August, while discussions about compensation and the transition process continued (Aug. 26 meeting). The ongoing need for a CDL driver and concerns about the availability of other road crew members were persistent issues.

Another big topic was reviewing the current equipment inventory and identifying discrepancies in records, including missing inventory lists and tools, and determining who is responsible for overseeing the list (Oct. 28 meeting). The decision to use ARPA funds to replace needed missing items was made.

At the beginning of 2025, Jeff Cathcart was made permanent road foreman. The road crew members include Chuck Steady, David Sabatini and Jim Hayes.

### Selectboard Clerk and Treasurer Transitions

The selectboard clerk position was vacant for a significant portion of the year, following a resignation in early June (May 13 meeting). Martin Fjeld continued to write the current minutes as town clerk while the board looked to fill the role. Other volunteers, like Luci Stevens, assisted with minute-taking (Aug. 26 meeting). However, this vacant role hampered administrative functions for the board. An advertisement for the position was posted on the website and in the local papers with details about various responsibilities, hours, and pay (Aug. 7 meeting).

Longtime Town Treasurer Vickee Whiting submitted her letter of retirement over the summer with the intent to leave the position by year's end (June 24 meeting). The selectboard was tasked with reviewing and approving the treasurer job description and posting an advertisement to fill the position. Heidi Larson was introduced as the new town treasurer in November, with Vickee Whiting remaining as assistant treasurer during the transition (Nov. 25 meeting).

### Other Personnel Matters

Other personnel-related issues arose throughout the year. The need for a comprehensive personnel policy was discussed in April (April 22 meeting). The town also addressed several new policies and positions, including an ethics liaison, Luci Stevens. (Aug. 26 meeting). The selectboard agreed in November to fund the animal control officer position. Aaron Todd was appointed to the tole, with Timmi Moffi as assistant animal control officer (Nov. 11 meeting).

Discussions took place regarding compensation for various positions, including the road foreman and road crew, with some decisions made to revisit rates later. A new line item was added to the 2025-2026 budget for DISA coordinator for $1,000 (Jan. 13, 2025 meeting)

Contract discussions included negotiating a multiyear contract with the Brandon Fire Department. Final numbers negotiated for the three-year contract are $5,000 – 9 months, $7,000 – 12 months, $8000 – 12 months (Nov. 21 meeting) and a plowing contract with Dave Sabatini for winter plowing, not to exceed $800 (Oct. 28 meeting).

A number of positions are open for residents to vote on for the March 4, 2025 election, including town meeting moderator, trustee of public funds, lister, delinquent tax collector, town clerk, cemetery commissioner and selectboard.

Diane O'Classen announced she will not be running for reelection of her current position on the selectboard (Jan. 13, 2025). Tammy Walsh has taken the oath of office, and it has been filed for her position on the cemetery commission (Jan. 13, 2025).`
        } // Will hold { Welcome, Roads, Grants }
      };
    },
    created() {
      /* this.fetchTownMapping(); */
    },
    methods: {
      setActiveSection(section) {
        this.activeSection = section;
        // Scroll the main content area back to the top when changing sections
        this.$nextTick(() => {
          const mainElement = document.querySelector('.main');
          if (mainElement) {
            mainElement.scrollTop = 0;
          }
        });
      },
      handleMobileSelection() {
        // If Sign Up option is selected, navigate to the home page
        if (this.activeSection === 'SignUp') {
          this.$router.push('/');
          // Reset the dropdown to a valid content section
          this.activeSection = 'Welcome';
        }
      },
      /* async fetchTownMapping() {
        try {
          // Get the Brandon document from townMapping
          const docRef = doc(db, 'townMapping', 'Brandon');
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Assuming structure: { TownMeeting: { content: { Welcome: '...', Roads: '...', Grants: '...' } } }
            this.content = data.TownMeeting.content;
          } else {
            console.error('No such document!');
          }
        } catch (err) {
          console.error('Error fetching town mapping:', err);
        } finally {
          this.loading = false;
        }
      }, */
      renderMarkdown(content) {
        const dirty = marked(content || '');
        const clean = DOMPurify.sanitize(dirty);
        return clean;
      },
    }
  };
  </script>
  
  <style scoped>
  .app-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  
  .content-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    overflow: hidden;
    margin-top: 64px;
  }
  
  .empty-left {
    display: flex;
    flex: 1;
    background-color: #fffff8;
  }
  
  .left-sidenotes, .right-sidenotes {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: #fffff8;
    padding: 20px;
  }
  
  .main {
    flex: 2;
    background-color: #fffff8;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-width: 1200px;
  }
  
  .settings-header {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 24px;
    color: #545051;
  }
  
  .subhead {
    font-size: 1rem;
    font-weight: 400;
    cursor: pointer;
    color: #545051;
    margin: 0 0 16px 0;
    padding: 0;
    line-height: 1.4;
  }
  
  .subhead.active {
    text-decoration: underline dashed;
    text-underline-offset: 2px;
  }

  .content{
    margin-top: 20px;
  }

/* Markdown Styles */
  ::v-deep p {
    margin-bottom: 1rem;
  }
  ::v-deep p:has(strong),
  ::v-deep p:has(b) {
    margin-bottom: 0.75rem;
  }

  ::v-deep h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  ::v-deep h4 {
    font-size: 1.25rem;
  }
  
  @media (max-width: 768px) {
    .mobile-select {
      display: block;
    }
    .empty-left,
    .left-sidenotes,
    .right-sidenotes {
      display: none;
    }
    .main {
      flex: 1;
      max-width: 100%;
      padding: 0 1rem;
    }
  }
  
  @media (min-width: 768px) {
    .mobile-select {
      display: none;
    }
    .empty-left, .right-sidenotes {
      display: none;
    }
    .left-sidenotes {
      display: flex;
    }
    .main {
      flex: 2;
      max-width: 100%;
      padding: 0 2rem;
    }
  }
  
  @media (min-width: 1024px) {
    .empty-left, .right-sidenotes, .left-sidenotes {
      display: flex;
    }
    .main {
      flex: 3;
      max-width: 1200px;
      padding: 0;
    }
  }
  
  .mobile-select {
    margin-bottom: 24px;
    position: relative;
    width: max-content;
  }
  
  .mobile-select select {
    font-size: 32px;
    font-weight: 600;
    color: #545051;
    padding-top: 24px;
    
    border: none;
    border-bottom: 1px dashed #545051;
    background-color: #fffff8;
    cursor: pointer;
    appearance: none;
    width: 100%;
  }
  
  .mobile-select select:focus {
    outline: none;
    border-bottom: 2px dashed #545051;    
    
  }
  </style>
  